import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "hvac-web-design-colorado",
  metaTitle: "HVAC Web Design in Colorado | Lead-Generating HVAC Websites | Colorado Web Impressions",
  metaDescription:
    "HVAC websites built for Colorado — fast, mobile-first, and tuned for emergency calls and high-ticket replacements. You own it. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "HVAC marketing", to: "/hvac-marketing-colorado" },
  ],
  currentLabel: "HVAC Web Design Colorado",
  eyebrow: "HVAC Web Design — Colorado",
  h1: (
    <>
      HVAC <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Fast, mobile-first HVAC websites built to convert calls — not just look pretty. Owner-led from Colorado Springs.",
  intro:
    "Most Colorado HVAC websites are built like brochures, then bolted onto a national page-builder template that loads slowly on mobile. We hand-code Colorado HVAC websites that load fast, surface NATE/EPA trust signals up top, keep emergency click-to-call sticky on every page, and close the high-ticket replacements with financing badges and proof. You own every file from day one.",
  definition: {
    term: "HVAC web design in Colorado",
    body: "HVAC web design is the practice of building websites specifically for heating and cooling companies — with mobile-first speed, sticky emergency click-to-call, NATE/EPA trust signals, financing visibility, and conversion-tested layouts that turn Colorado homeowners into booked service calls.",
  },
  whatWeDo: [
    { title: "Hand-coded, mobile-first builds", body: "No bloated page builders. Every Colorado HVAC site we ship loads fast on mobile — where 70%+ of HVAC searches happen." },
    { title: "Sticky emergency click-to-call", body: "Mobile-first sticky CTA bar with click-to-call and after-hours emergency messaging — visible on every page, every scroll." },
    { title: "Trust signals above the fold", body: "NATE certification, EPA, BBB, financing badges, and Google review stars surfaced in the hero — not buried three scrolls deep." },
    { title: "Service-area pages baked in", body: "Dedicated pages for every Colorado market you serve — Colorado Springs, Denver, Castle Rock, Fort Collins — each with its own content, schema, and Map Pack targeting." },
    { title: "Conversion-tested forms", body: "Short, mobile-friendly lead forms with project-type qualifiers — built to capture leads, not interrogate prospects." },
    { title: "You own everything", body: "You own the domain, the code, and every file — from day one and forever. No platform lock-in, no monthly ransom." },
  ],
  whyThisMarket: [
    {
      heading: "Colorado HVAC searches are mobile and urgent",
      body: "70%+ of Colorado HVAC searches happen on mobile, and most are emergency-intent. Sites that don't load fast and don't make calling obvious leak revenue every minute. We design every page around the click-to-call action.",
    },
    {
      heading: "Trust signals close the high-ticket jobs",
      body: "Furnace and AC replacements are $5K–$15K decisions. Colorado homeowners need to see NATE/EPA credentials, financing options, and real reviews before they pick up the phone. We surface all of it in the first scroll.",
    },
    {
      heading: "Page speed is an SEO ranking factor",
      body: "Google ranks fast Colorado HVAC sites higher in both organic and Map Pack results. The slower your site, the harder your SEO has to work to compensate. We hand-code so speed is built in — not retrofitted.",
    },
  ],
  relatedLinks: [
    { to: "/hvac-marketing-colorado", label: "HVAC marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/hvac-seo-colorado", label: "HVAC SEO in Colorado" },
    { to: "/hvac-google-maps-colorado", label: "HVAC Google Maps optimization in Colorado" },
    { to: "/colorado-springs-web-design", label: "Colorado Springs web design" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/denver-digital-marketing", label: "Denver digital marketing" },
    { to: "/case-studies", label: "Colorado HVAC case studies" },
    { to: "/wix-vs-custom-website-colorado-hvac", label: "Wix vs custom website for HVAC" },
    { to: "/squarespace-vs-custom-website-colorado-hvac", label: "Squarespace vs custom website for HVAC" },
    { to: "/godaddy-vs-custom-website-colorado-hvac", label: "GoDaddy vs custom website for HVAC" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does an HVAC website cost in Colorado?", a: "Our Starter HVAC website is $1,850 one-time for 4 pages, and our Pro HVAC website is $2,750 for up to 10 pages — you own it outright forever, no monthly platform fees. Larger HVAC companies serving multiple Colorado markets often need a custom build, which we'll quote transparently up front." },
    { q: "How long does it take to build an HVAC website in Colorado?", a: "Typically 3–5 weeks from kickoff to launch for a standard HVAC site. Most delays happen waiting on client-provided photos and content. If you need us to write the content, we have an HVAC-specialized add-on." },
    { q: "Do I own my HVAC website after it's built?", a: "Yes — completely. You own the domain, the code, and every file from day one. We never hold HVAC websites hostage. If you ever leave us, you walk away with everything intact." },
    { q: "Will my new HVAC website rank in Google Maps and organic search?", a: "Every Colorado HVAC site we ship is built with the SEO foundation already in place — schema, fast page speed, mobile-first design, service-area pages, and Google Business Profile alignment. SEO and Map Pack work compound on that foundation." },
  ],
  serviceName: "HVAC Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first HVAC websites for Colorado heating and cooling companies. Fast, conversion-tested, and built for emergency call capture.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/hvac-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
